import type { CRMUser } from '../types';

export type NamedUser = Pick<CRMUser, 'uid' | 'displayName'> &
  Partial<Pick<CRMUser, 'email' | 'company'>>;

export function crmUserLabel(user: NamedUser | undefined): string {
  return (user?.displayName || '').trim()
    || (user?.email || '').trim()
    || (user?.company || '').trim();
}

function findNamedUser(teamMembers: NamedUser[], id: string): NamedUser | undefined {
  return teamMembers.find((u) => u.uid === id)
    || teamMembers.find((u) => (u.email || '').trim() === id);
}

/** Stored in `actionRequired` when waiting on a non-CRM party; label in `actionRequiredNote`. */
export const ACTION_REQUIRED_EXTERNAL_SENTINEL = '__lctf_external__';

export function isActionRequiredExternal(actionRequired?: string | null): boolean {
  return actionRequired === ACTION_REQUIRED_EXTERNAL_SENTINEL;
}

/**
 * Short chip text for task table rows (first name or first word of external label).
 */
export function getActionRequiredShortLabel(
  actionRequired: string | undefined,
  actionRequiredNote: string | undefined,
  teamMembers: NamedUser[],
): string {
  if (!actionRequired) return '';
  if (isActionRequiredExternal(actionRequired)) {
    const raw = (actionRequiredNote || '').trim();
    if (!raw) return 'External';
    const first = raw.split(/\s+/)[0];
    return first.length > 12 ? `${first.slice(0, 11)}…` : first;
  }
  const full = crmUserLabel(findNamedUser(teamMembers, actionRequired));
  if (!full) return actionRequired.includes('@') ? actionRequired.split('@')[0] : 'Action';
  return full.split(' ')[0];
}

/**
 * Full display for detail panels and summaries.
 */
export function getActionRequiredFullLabel(
  actionRequired: string | undefined,
  actionRequiredNote: string | undefined,
  teamMembers: NamedUser[],
): string {
  if (!actionRequired) return '';
  if (isActionRequiredExternal(actionRequired)) {
    const raw = (actionRequiredNote || '').trim();
    return raw ? `External: ${raw}` : 'External';
  }
  return crmUserLabel(findNamedUser(teamMembers, actionRequired))
    || (actionRequired.includes('@') ? actionRequired : '')
    || 'Unknown';
}
